import React, { useState, useEffect } from 'react';
import { X, FileText, CheckCircle2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, getUserPhone } from '../lib/api';

const STORAGE_KEY = 'gynovate_letterhead';

const EMPTY = {
  doctor_name: '',
  qualifications: '',
  reg_no: '',
  clinic_name: '',
  address: '',
  phone: '',
  email: '',
  timings: '',
  logo_url: '',
};

export function getStoredLetterhead() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Return null if letterhead is essentially empty
    const hasAnything = Object.values(data || {}).some(v => (v || '').toString().trim().length > 0);
    return hasAnything ? data : null;
  } catch {
    return null;
  }
}

export default function LetterheadModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2 MB'); return; }
    const phone = getUserPhone();
    if (!phone) { toast.error('Please sign in first'); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('phone', phone);
      fd.append('file', file);
      const res = await fetch(`${API}/api/letterhead/logo`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setForm((prev) => {
        const next = { ...prev, logo_url: data.logo_url };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
      });
      toast.success('Clinic logo uploaded — it will appear on your exported documents');
    } catch (err) {
      toast.error(err.message || 'Logo upload failed');
    }
    setUploadingLogo(false);
    e.target.value = '';
  };

  useEffect(() => {
    if (open) {
      const stored = getStoredLetterhead();
      setForm(stored ? { ...EMPTY, ...stored } : EMPTY);
      setSaved(false);
      if (!stored) {
        const phone = getUserPhone();
        if (phone) {
          fetch(`${API}/api/letterhead/${encodeURIComponent(phone)}`)
            .then((r) => r.json())
            .then((d) => { if (d.letterhead) setForm({ ...EMPTY, ...d.letterhead }); })
            .catch(() => {});
        }
      }
    }
  }, [open]);

  if (!open) return null;

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setSaved(false);
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    const phone = getUserPhone();
    if (phone) {
      fetch(`${API}/api/letterhead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, letterhead: form }),
      }).catch(() => {});
    }
    setSaved(true);
    toast.success('Letterhead saved once — it will now appear on all your generated documents.');
    setTimeout(() => {
      onClose?.();
    }, 600);
  };

  const handleClear = () => {
    localStorage.removeItem(STORAGE_KEY);
    const phone = getUserPhone();
    if (phone) {
      fetch(`${API}/api/letterhead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, letterhead: {} }),
      }).catch(() => {});
    }
    setForm(EMPTY);
    toast('Letterhead cleared. Documents will use blank placeholders again.', { icon: '🧹' });
  };

  const preview = (() => {
    const parts = [];
    const dn = (form.doctor_name || '').trim();
    const q = (form.qualifications || '').trim();
    if (dn) parts.push(`${dn}${q ? ', ' + q : ''}`);
    if ((form.reg_no || '').trim()) parts.push(`Reg. No.: ${form.reg_no.trim()}`);
    if ((form.clinic_name || '').trim()) parts.push(form.clinic_name.trim());
    if ((form.address || '').trim()) parts.push(form.address.trim());
    const contactBits = [];
    if ((form.phone || '').trim()) contactBits.push(`Ph: ${form.phone.trim()}`);
    if ((form.email || '').trim()) contactBits.push(`Email: ${form.email.trim()}`);
    if (contactBits.length) parts.push(contactBits.join(' | '));
    if ((form.timings || '').trim()) parts.push(`Timings: ${form.timings.trim()}`);
    return parts.join('\n');
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4"
      onClick={onClose}
      data-testid="letterhead-modal-overlay"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="letterhead-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-raspberry-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-raspberry-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900">Clinic Letterhead</h2>
              <p className="text-xs text-slate-500">Auto-fills into every generated document</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
            data-testid="letterhead-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Clinic logo upload */}
          <div className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-slate-300 bg-slate-50">
            {form.logo_url ? (
              <img src={`${API}${form.logo_url}`} alt="Clinic logo" className="h-12 w-auto rounded-md border border-slate-200 bg-white object-contain" data-testid="letterhead-logo-preview" />
            ) : (
              <div className="h-12 w-12 rounded-md bg-slate-200 flex items-center justify-center text-slate-400 text-lg">🏥</div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-700">Clinic Logo (optional)</p>
              <p className="text-[11px] text-slate-500">PNG/JPG under 2 MB — appears on exported PDFs & Word docs</p>
            </div>
            <label className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 cursor-pointer whitespace-nowrap" data-testid="letterhead-logo-upload-btn">
              {uploadingLogo ? 'Uploading…' : form.logo_url ? 'Change' : 'Upload'}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleLogoUpload} disabled={uploadingLogo} />
            </label>
            {form.logo_url && (
              <button onClick={() => setForm((p) => ({ ...p, logo_url: '' }))} className="text-xs text-slate-400 hover:text-red-500" data-testid="letterhead-logo-remove">✕</button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Doctor Name *</label>
              <input
                type="text"
                value={form.doctor_name}
                onChange={handleChange('doctor_name')}
                placeholder="Dr. A. Sharma"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-raspberry-500 focus:ring-1 focus:ring-raspberry-500 outline-none"
                data-testid="letterhead-doctor-name"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Qualifications</label>
              <input
                type="text"
                value={form.qualifications}
                onChange={handleChange('qualifications')}
                placeholder="MBBS, MD (OBGY), DGO"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-raspberry-500 focus:ring-1 focus:ring-raspberry-500 outline-none"
                data-testid="letterhead-qualifications"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Registration Number</label>
              <input
                type="text"
                value={form.reg_no}
                onChange={handleChange('reg_no')}
                placeholder="GMC-12345 / MMC-56789"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-raspberry-500 focus:ring-1 focus:ring-raspberry-500 outline-none"
                data-testid="letterhead-reg-no"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Clinic / Hospital Name</label>
              <input
                type="text"
                value={form.clinic_name}
                onChange={handleChange('clinic_name')}
                placeholder="Sunrise Women's Clinic"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-raspberry-500 focus:ring-1 focus:ring-raspberry-500 outline-none"
                data-testid="letterhead-clinic-name"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">Address</label>
            <textarea
              value={form.address}
              onChange={handleChange('address')}
              rows={2}
              placeholder="2nd Floor, Sunrise Plaza, MG Road, Bengaluru — 560001"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-raspberry-500 focus:ring-1 focus:ring-raspberry-500 outline-none resize-none"
              data-testid="letterhead-address"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={handleChange('phone')}
                placeholder="+91 98765 43210"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-raspberry-500 focus:ring-1 focus:ring-raspberry-500 outline-none"
                data-testid="letterhead-phone"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                placeholder="clinic@example.com"
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-raspberry-500 focus:ring-1 focus:ring-raspberry-500 outline-none"
                data-testid="letterhead-email"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">Consultation Timings (optional)</label>
            <input
              type="text"
              value={form.timings}
              onChange={handleChange('timings')}
              placeholder="Mon–Sat, 10am–1pm & 5pm–8pm"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-raspberry-500 focus:ring-1 focus:ring-raspberry-500 outline-none"
              data-testid="letterhead-timings"
            />
          </div>

          {/* Live Preview */}
          {preview && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Preview</p>
              <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 font-mono whitespace-pre-wrap" data-testid="letterhead-preview">
{preview}
              </pre>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
            <strong>Privacy:</strong> Saved securely to your Gynovate account (so it works on all your devices) and used only to place your letterhead on documents you generate.
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-2 bg-slate-50">
          <button
            onClick={handleClear}
            className="px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 flex items-center gap-1.5"
            data-testid="letterhead-clear-btn"
          >
            <Trash2 className="w-4 h-4" /> Clear
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200"
              data-testid="letterhead-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!(form.doctor_name || '').trim()}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-raspberry-600 hover:bg-raspberry-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              data-testid="letterhead-save-btn"
            >
              {saved ? <><CheckCircle2 className="w-4 h-4" /> Saved</> : 'Save Letterhead'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
